import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getAuthHeaders, makeApiCall, getApiUrl } from '../utils/apiConfig';
import type { ApiKeyDataScope, ApiKeyScopeOptions } from '../types/apiKeyDataScope';
import type { ShopifyRelatedProductsStatus, StoreRelatedPicksView } from '../types/relatedProducts';
import type { StorePreorderGoLiveInput, StorePreorderGoLiveView } from '../types/storePreorderGoLive';

// Retry configuration for handling 429 rate limit errors
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

async function axiosWithRetry<T>(
  config: AxiosRequestConfig,
  retries = MAX_RETRIES
): Promise<AxiosResponse<T>> {
  try {
    return await axios(config);
  } catch (error) {
    const axiosError = error as AxiosError;
    
    if (axios.isCancel(error) || axiosError.code === 'ERR_CANCELED') {
      throw error;
    }

    // Only retry on 429 (rate limit) errors
    if (axiosError.response?.status === 429 && retries > 0) {
      const retryAfter = axiosError.response.headers['retry-after'];
      const delay = retryAfter 
        ? parseInt(retryAfter) * 1000 
        : BASE_DELAY_MS * Math.pow(2, MAX_RETRIES - retries);
      
      console.log(`Rate limited, retrying in ${delay}ms (${retries} retries left)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return axiosWithRetry<T>(config, retries - 1);
    }
    
    throw error;
  }
}

export interface Product {
  id: string;
  /** Source of description, specs and images; sent always, shown only with flag `catalog_field_provenance`. */
  fieldSources?: Partial<
    Record<
      'description' | 'shortDescription' | 'features' | 'images' | 'specs',
      { source: 'scraped' | 'official' | 'ai' | 'manual'; confidence?: number }
    >
  >;
  name?: string;
  model: string;
  price: number;
  priceFormatted: string;
  currency?: 'USD' | 'CAD' | string;
  detailedLink?: string;
  category: string;
  /** Classification fields the dealer view checks for agreement. */
  categorySlug?: string;
  categoryPath?: string[];
  productTypeExpanded?: string;
  brand: string;
  images: string[];
  specifications: Record<string, string>;
  description: string;
  shortDescription?: string;
  productType: 'camera' | 'lens' | 'accessory';
  accessoryType?: string;
  sku?: string;
  upc?: string;
  ean?: string;
  jan?: string;
  asin?: string;
  mpn?: string;
  /** Identifier fills. Full product only; a Barcode Lookup MPN badges from `source: 'barcodelookup'`. */
  enrichment?: { filledFrom?: Array<{ source?: string; fields?: string[] }> };
  gtin?: string;
  countryOfOrigin?: string;
  vendorId?: string;
  vendorName?: string;
  connectorId?: string;
  source?: string;
  sourceId?: string;
  createdAt: any;
  updatedAt: any;
  // Rich content
  features?: string[];
  highlights?: string[];
  featureSections?: Array<{ title: string; body: string; imageUrl?: string }>;
  inTheBox?: Array<{ name: string; quantity?: number; sku?: string }>;
  videos?: Array<{ url: string; title?: string; type?: string; thumbnailUrl?: string }>;
  documentation?: Array<{ url: string; title: string; type: string }>;
  dimensions?: { length?: number; width?: number; height?: number; weight?: number; unit?: string; weightUnit?: string };
  shippingDimensions?: { length?: number; width?: number; height?: number; weight?: number; unit?: string; weightUnit?: string };
  /** Estimated packaged weight/box; shown as Estimated under flag `shipping_weight_fallback`. */
  estimatedShippingDimensions?: { length?: number; width?: number; height?: number; weight?: number; unit?: string; weightUnit?: string; source?: string; confidence?: number; wouldPush?: boolean };
  tags?: string[];
  // Sale state
  isSaleActive?: boolean;
  regularPrice?: number;
  regularPriceFormatted?: string;
  savingsAmount?: number;
  savingsFormatted?: string;
  // Pricing details
  pricing?: {
    msrp?: number;
    retail?: number;
    map?: number;
    /** Dealer cost. API strips this unless the store is verified for this brand. */
    wholesale?: number;
    /**
     * Promaster's warehouse dealer cost (flag `promaster_dealer_cost`), with the
     * date Promaster last reported it. Stripped like `wholesale`.
     */
    promasterDealerCost?: { amount: number; currency: string; asOf: string; stale: boolean };
    /** Instant rebate + window: while active, the advertised floor is map − instantRebate. */
    instantRebate?: number;
    rebateStartAt?: any;
    rebateEndAt?: any;
    mapStartAt?: any;
    manufacturerPriceAsOf?: any;
    manufacturerPriceEndAt?: any;
    salePrice?: number;
    salePriceStartAt?: any;
    salePriceEndAt?: any;
    currency?: string;
    priceAsOf?: any;
    priceLastChecked?: any;
    vendorPriceAsOf?: any;
    /** Sync price verification (flag `sync_price_verification_alerts`). */
    verification?: {
      status: 'verified' | 'unverified';
      lastVerifiedAt?: any;
      lastVerifiedPrice?: number;
      unverifiedSince?: any;
      reason?: 'fetch_failed' | 'not_in_results' | 'discontinued';
    };
    suspiciousDiscountReview?: {
      status: 'pending' | 'approved' | 'rejected';
      advertisedPrice: number;
      floor: number;
      floorKind: 'map' | 'msrp';
      firstSeenAt?: string;
      reviewedAt?: string;
      reviewedBy?: string;
      reviewNote?: string;
      catalogSnapshot?: {
        msrp?: number | null;
        retail?: number | null;
        salePrice?: number | null;
        map?: number | null;
        marketMsrp?: number | null;
      };
    };
    /** Flag `price_change_review`: a price change is waiting for the store's approval; `approved*` is what the connected platform still shows. */
    priceReview?: { status: 'pending'; itemId: string; dueAt: string | null; approvedFrom: string };
    approvedPrice?: number | null;
    approvedCompareAtPrice?: number | null;
    approvedInstantRebate?: number | null;
    missingAdvertisedPriceReview?: {
      status: 'pending' | 'resolved';
      firstSeenAt?: string;
      lastSeenAt?: string;
      resolvedAt?: string;
      catalogSnapshot?: {
        msrp?: number | null;
        retail?: number | null;
        salePrice?: number | null;
        map?: number | null;
        marketMsrp?: number | null;
      };
    };
  };
  marketPricing?: {
    msrp?: number;
    msrpSource?: string;
    streetPriceLow?: number;
    streetPriceHigh?: number;
    streetPriceAvg?: number;
    dealerPrices?: Array<{ dealer: string; price: number; url?: string; inStock?: boolean; observedAt?: string }>;
    checkedAt?: string;
    confidence?: number;
    refreshingAt?: string;
  };
  // Color/kit variants
  variants?: Array<{
    sku: string;
    upc?: string;
    ean?: string;
    asin?: string;
    mpn?: string;
    optionName: string;
    optionValue: string;
    title?: string;
    kind?: 'kit' | 'bundle' | 'configuration' | 'style';
  }>;
  parentSku?: string;
  variantGroupId?: string;
  // Manufacturer stock status — informational for store context only
  manufacturerStock?: {
    status: 'in_stock' | 'out_of_stock' | 'backorder' | 'preorder' | 'discontinued' | 'unknown';
    label?: string;
    checkedAt?: string;
  };
  isProOnly?: boolean;
  proSiteUrl?: string;
  // Product lifecycle tracking
  status?: 'active' | 'archived' | 'discontinued';
  isArchived?: boolean;
  archivedReason?: string;
  archivedAt?: any;
  lastKnownPrice?: number;
  lastKnownMsrp?: number;
  // Date tracking
  launchDate?: string;
  launchDateRaw?: string;
  launchDateSource?: string;
  launchDates?: any;
  /**
   * Pre-release stage (flag `preorder_lifecycle`): served by the store browser while the
   * flag is on for the store, and attached by the admin browser from `getAdminPreReleaseProducts`.
   */
  lifecycle?: {
    stage: 'announced' | 'preorder' | 'released';
    releaseDate?: string | null;
    preorderOpensAt?: string | null;
    source?: string;
    updatedAt?: string;
  };
  firstImportedAt?: any;
  lastScrapedAt?: any;
  /** The API hid dealer cost / reimbursement: this brand's dealer authorization is not verified for the store */
  vendorPrivateLocked?: boolean;
}

// Legacy interface for backward compatibility
export interface CameraProduct extends Product {
  productType: 'camera';
}

export type CatalogProductStatus = 'active' | 'archived' | 'all';

/** Product list order. Name only: the catalog's existing indexes serve it without a scan. */
export type CatalogSort = 'name_asc' | 'name_desc';

export interface ProductResponse {
  success: boolean;
  data: Product[];
  count: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
    /** Counts for the full filtered result set (same filters as `total`), when returned by the API */
    statusCounts?: Partial<Record<'active' | 'archived' | 'draft' | 'discontinued', number>>;
    /**
     * Synced discontinued products the server merged into an Active result
     * (`includeSyncedDiscontinued`), after the same filters; absent otherwise.
     */
    discontinuedInSync?: number;
  };
}

/**
 * A sync-selection write the plan cap refused (`SYNC_SELECTION_CAP` for
 * products, `SYNC_SELECTION_BRAND_CAP` for brands). Carries the numbers so the
 * browser can explain it instead of showing a generic failure.
 */
export class SyncSelectionCapError extends Error {
  readonly code: 'SYNC_SELECTION_CAP' | 'SYNC_SELECTION_BRAND_CAP';
  readonly limit: number;
  readonly current: number;
  readonly attempted: number;
  readonly planName: string | null;

  constructor(
    message: string,
    code: 'SYNC_SELECTION_CAP' | 'SYNC_SELECTION_BRAND_CAP',
    details: { limit: number; current: number; attempted: number; planName: string | null },
  ) {
    super(message);
    this.name = 'SyncSelectionCapError';
    this.code = code;
    this.limit = details.limit;
    this.current = details.current;
    this.attempted = details.attempted;
    this.planName = details.planName;
  }
}

export interface StoreSyncSelection {
  selectedProductIds: string[];
  syncAll: boolean;
  /** Set when the selection was read for one integration. */
  integrationId?: string | null;
  /** True when that integration is using the store's selection rather than its own. */
  inheritsFromOrganization?: boolean;
  filters?: {
    brands?: string[];
    categories?: string[];
    priceMin?: number | null;
    priceMax?: number | null;
  };
  lastUpdated?: any;
}

/** One row of the store's sync selections: the store's own, or one integration's. */
export interface StoreVariantGroupMember {
  id: string;
  sku: string;
  name: string;
  optionName: string;
  optionValue: string;
  selected: boolean;
}

export interface StoreVariantGroup {
  groupId: string;
  members: StoreVariantGroupMember[];
}

export interface StoreSyncSelectionSummary {
  integrationId: string | null;
  inheritsFromOrganization: boolean;
  productCount: number;
  updatedAt?: any;
}

// Legacy interface for backward compatibility
export interface CameraResponse extends ProductResponse {
  data: CameraProduct[];
}

export interface ApiKey {
  id: string;
  name: string;
  key: string;
  createdAt: any;
  lastUsed?: any;
  isActive: boolean;
  /**
   * Organization the key is billed and scoped to. Present on `GET /api-keys`
   * even when this client previously ignored it. Missing on keys created
   * before the field was stored.
   */
  organizationId?: string | null;
  /** Endpoint path prefixes or "*" — empty/missing means no API-key access. */
  permissions?: string[];
  /** Backend flag `scoped_api_keys`: narrows the organization scope; missing = inherit. */
  dataScope?: ApiKeyDataScope;
}

export interface BrandRegistryItem {
  id: string;
  slug: string;
  name: string;
  logoUrl?: string | null;
  website?: string | null;
  status?: string;
  vendorOrgId?: string | null;
}

export interface MapPricing {
  id: string;
  cameraId: string;
  mapPrice: number;
  effectiveDate: any;
  endDate?: any;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface StoreProductOverride {
  id: string;
  organizationId: string;
  productId: string;
  model?: string | null;
  brand?: string | null;
  sku?: string | null;
  overrideTitle?: string | null;
  overrideDescription?: string | null;
  /** When set (including []), replaces Motzu auto taxonomy tags on Shopify sync. */
  overrideTags?: string[] | null;
  overridePrice?: number | null;
  overridePriceStartAt?: string | Date | null;
  overridePriceEndAt?: string | Date | null;
  ignoreSalePrice?: boolean;
  ignoreRebate?: boolean;
  allowSellDiscontinued?: boolean;
  /** AI copy draft awaiting the store's review (flag store_ai_copy); never pushed until accepted. */
  aiCopyDraft?: StoreAiCopyDraft | null;
  /** Approved bulk-AI spec suggestions (flag bulk_ai_operations), label -> value; store-scoped, never catalog data. */
  aiSpecSuggestions?: Record<string, string> | null;
  /** Specs the store took from those suggestions; store-scoped, not synced yet. */
  overrideSpecs?: Record<string, string> | null;
  createdAt?: any;
  updatedAt?: any;
}

/** Mirrors `StoreAiCopyDraft` in src/types/storeAiCopy.ts. */
export interface StoreAiCopyDraft {
  title: string | null;
  description: string | null;
  generatedAt: string | Date | null;
  generatedBy?: string | null;
  rulesHash: string;
  model?: string | null;
  /** 0..1 similarity to the catalog copy; a near copy is flagged for the store. */
  titleSimilarity: number | null;
  descriptionSimilarity: number | null;
  nearCopy: boolean;
  bannedPhraseHits: string[];
}

export interface ErpSyncAuditEvent {
  id: string;
  organizationId: string;
  integrationId: string;
  productId: string;
  productName?: string | null;
  productSku?: string | null;
  platform: string;
  shopDomain?: string | null;
  shopifyProductId?: string | null;
  syncRunId?: string | null;
  status: 'created' | 'updated' | 'skipped' | 'error';
  timestamp: string;
  pushed?: {
    price: number | null;
    compareAtPrice: number | null;
    title?: string | null;
    sku?: string | null;
  } | null;
  catalog?: {
    retail?: number | null;
    map?: number | null;
    msrp?: number | null;
    salePrice?: number | null;
    instantRebate?: number | null;
    basePrice?: number | null;
  } | null;
  /** Present only for events recorded with flag `sync_audit_field_history` on. */
  fieldChanges?: Array<{ field: string; before: unknown; after: unknown }> | null;
  skipCode?: string | null;
  error?: string | null;
}

export type ShopifyMapAuditIssue =
  | 'below_map'
  | 'suspicious_discount'
  | 'stale_price'
  | 'stale_compare_at'
  | 'missing_live_price'
  | 'unmapped_variant';

export interface ShopifyMapAuditSummary {
  id: string;
  organizationId: string;
  integrationId: string;
  shopDomain?: string | null;
  retailerName?: string | null;
  status: 'running' | 'complete' | 'error';
  auditRunId: string;
  mappingsChecked: number;
  mappingsOffset?: number;
  mappingsTotal?: number;
  phase?: 'mapped' | 'unmapped' | 'complete';
  variantsChecked: number;
  variantsSkippedForeign: number;
  unmappedVariant: number;
  belowMap: number;
  suspiciousDiscount: number;
  stalePrice: number;
  staleCompareAt: number;
  missingLivePrice: number;
  fetchErrors: number;
  newViolations: number;
  resolvedViolations: number;
  startedAt: string | Date;
  completedAt?: string | Date | null;
  error?: string | null;
  /** Plan cap on mapped products this run checked (flag map_audit_ladder); absent = no cap. */
  mapAuditCap?: number;
  /** Mapped products left out by `mapAuditCap` ("N more not audited on Free Preview"). */
  productsNotAuditedByPlan?: number;
}

export interface ShopifyMapAuditFinding {
  id: string;
  auditRunId: string;
  organizationId: string;
  integrationId: string;
  productId: string;
  productName?: string | null;
  productSku?: string | null;
  productBrand?: string | null;
  productModel?: string | null;
  hostProductId?: string | null;
  shopifyProductId: string;
  shopifyVariantId: string;
  shopifySku?: string | null;
  shopifyVariantTitle?: string | null;
  shopifyAdminUrl?: string | null;
  shopifyStatus?: string | null;
  livePrice: number | null;
  liveCompareAtPrice: number | null;
  intendedPrice: number | null;
  intendedCompareAtPrice: number | null;
  mapFloor: number | null;
  msrp: number | null;
  issues: ShopifyMapAuditIssue[];
  severity: 'critical' | 'warning' | 'ok';
  hasIssue: boolean;
  violationId?: string | null;
  error?: string | null;
  auditedAt: string | Date;
}

export interface ShopifyMapAuditResponse {
  success: boolean;
  organizationId: string;
  summary: ShopifyMapAuditSummary;
  findings: ShopifyMapAuditFinding[];
  violations?: Array<{
    id: string;
    productId: string;
    productModel: string;
    productBrand: string;
    mapPrice: number;
    actualPrice: number;
    status: string;
    source: string;
  }>;
}

export interface ErpSyncAuditResponse {
  success: boolean;
  productId: string;
  organizationId: string;
  events: ErpSyncAuditEvent[];
      mappings: Array<{
    id: string;
    integrationId: string;
    platform?: string;
    shopifyProductId: string | null;
    lastSyncedAt: string | null;
    syncMode?: string | null;
    syncPricing?: boolean;
    /** Shopify only: the integration's shop handle, to name the shop on per-integration controls. */
    shopDomain?: string | null;
    /** Shopify only: false when the integration turned Sync Products off. */
    syncProducts?: boolean;
    /** Shopify only: whether the product is in the integration's sync selection; unset when unknown. */
    inSyncSelection?: boolean;
    lastPushed?: {
      price?: number | null;
      compareAtPrice?: number | null;
      title?: string | null;
      sku?: string | null;
      status?: string | null;
      at?: string | null;
      syncRunId?: string | null;
      /** Where the pushed title and description came from (flag `copy_provenance`). */
      copyProvenance?: import('../utils/erpSyncAuditDisplay').CopyProvenance;
    } | null;
  }>;
  live: Array<{
    shopifyProductId: string;
    integrationId: string;
    shopDomain?: string | null;
    price: number | null;
    compareAtPrice: number | null;
    title: string | null;
    variantTitle?: string | null;
    variantCount?: number;
    skuMatched?: boolean;
    sku: string | null;
    status: string | null;
    updatedAt: string | null;
    adminUrl: string | null;
    error?: string | null;
  }>;
}

/** What a force resync may overwrite (POST /store/erp-resync). `forceCategories` is Lightspeed only. */
export interface ErpResyncOptions {
  forceDescription?: boolean;
  forceImages?: boolean;
  forcePricing?: boolean;
  forceCategories?: boolean;
}

/** `POST /integrations/:id/shopify-mappings/:productId/reset`. */
export interface ShopifyLinkResetResult {
  reset: true;
  /** The one-product run that matches the product again; always a new run. */
  runId: string;
  /** The sync already queued or running when the link was reset; `runId` starts after it. Null when none. */
  queuedBehindRunId: string | null;
  removedMappings: number;
  previousShopifyProductIds: string[];
}

export interface ErpProductResyncResponse {
  success: boolean;
  productId: string;
  addedToSyncSelection: boolean;
  results: Array<{
    integrationId: string;
    integrationName: string | null;
    platform?: string;
    shopDomain: string | null;
    status: 'success' | 'skipped' | 'error';
    shopifyId?: string;
    error?: string;
    warnings: Array<{ productId: string; message: string; code?: string }>;
  }>;
}

export interface GeneratedProductDescription {
  description: string;
  isAI: boolean;
}

class ApiService {
  // Use getApiUrl() to get the correct URL based on environment
  // This ensures development uses /api proxy and production uses api.motzulabs.com
  public get baseURL(): string {
    return getApiUrl();
  }
  private apiKey: string | null = null;
  private brandRegistryCache: Map<string, BrandRegistryItem> = new Map();
  private brandRegistryListCache: BrandRegistryItem[] | null = null;
  private brandRegistryFetchedAt = 0;
  private readonly brandRegistryTtlMs = 5 * 60 * 1000;

  setApiKey(apiKey: string | null) {
    this.apiKey = apiKey;
  }

  async getHeaders() {
    const headers = await getAuthHeaders();

    // Add impersonation context if present (admin acting on behalf of an org)
    if (typeof window !== 'undefined') {
      try {
        const impersonationState = window.localStorage.getItem('admin-impersonation');
        if (impersonationState) {
          const parsedState = JSON.parse(impersonationState);
          if (parsedState?.isImpersonating && parsedState?.org?.id) {
            headers['X-Impersonated-Organization-Id'] = parsedState.org.id;
          }
        }
      } catch (error) {
        console.warn('Failed to read impersonation state for API headers:', error);
      }
    }

    // Add API key if available
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }

    return headers;
  }

  private normalizeBrandKey(value: string): string {
    return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
  }

  async getBrandRegistry(forceRefresh = false): Promise<BrandRegistryItem[]> {
    const now = Date.now();
    if (!forceRefresh && this.brandRegistryListCache && now - this.brandRegistryFetchedAt < this.brandRegistryTtlMs) {
      return this.brandRegistryListCache;
    }

    const response = await makeApiCall('/brand-registry', {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to load brand registry');
    }
    const payload = await response.json();
    const brands: BrandRegistryItem[] = payload?.data || [];
    this.brandRegistryListCache = brands;
    this.brandRegistryFetchedAt = now;
    this.brandRegistryCache.clear();
    brands.forEach(brand => {
      if (brand.slug) {
        this.brandRegistryCache.set(this.normalizeBrandKey(brand.slug), brand);
      }
      if (brand.name) {
        this.brandRegistryCache.set(this.normalizeBrandKey(brand.name), brand);
      }
    });
    return brands;
  }

  async resolveBrandRegistry(brand: string): Promise<BrandRegistryItem | null> {
    if (!brand) return null;
    const key = this.normalizeBrandKey(brand);
    if (this.brandRegistryCache.has(key)) {
      return this.brandRegistryCache.get(key) || null;
    }

    await this.getBrandRegistry();
    if (this.brandRegistryCache.has(key)) {
      return this.brandRegistryCache.get(key) || null;
    }

    const response = await makeApiCall(`/brand-registry/resolve?name=${encodeURIComponent(brand)}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      return null;
    }
    const payload = await response.json();
    const resolved = payload?.data as BrandRegistryItem | null;
    if (resolved) {
      if (resolved.slug) {
        this.brandRegistryCache.set(this.normalizeBrandKey(resolved.slug), resolved);
      }
      if (resolved.name) {
        this.brandRegistryCache.set(this.normalizeBrandKey(resolved.name), resolved);
      }
    }
    return resolved;
  }

  /**
   * Public marketing showcase — latest camera for Canon, Nikon, and Sony.
   * No auth required. Fixed response size (max 3 products with full specs).
   */
  async getProductShowcase(): Promise<Product[]> {
    const response = await axiosWithRetry<{ success: boolean; data: Product[]; count: number }>({
      method: 'get',
      url: `${this.baseURL}/products/showcase`,
    });
    return response.data?.data ?? [];
  }

  /** Public detail for a current showcase product only (no auth). */
  async getProductShowcaseById(id: string): Promise<Product | null> {
    const response = await axiosWithRetry<{ success: boolean; data: Product }>({
      method: 'get',
      url: `${this.baseURL}/products/showcase/${encodeURIComponent(id)}`,
    });
    return response.data?.data ?? null;
  }

  // Product API methods (unified for cameras, lenses, and accessories)
  async getProducts(
    limit = 10, 
    offset = 0, 
    productType?: 'camera' | 'lens' | 'accessory',
    catalogStatus?: CatalogProductStatus,
    sort?: CatalogSort,
    /** `false` leaves announced and pre-order products out (MOT-699); omitted keeps them. */
    includeUnreleased?: boolean
  ): Promise<ProductResponse> {
    const endpoint = '/products';
    const params: any = { limit, offset, slim: 'true' };
    if (sort && sort !== 'name_asc') params.sort = sort;
    if (includeUnreleased === false) params.includeUnreleased = 'false';
    if (productType) {
      params.productType = productType;
    }
    if (catalogStatus && catalogStatus !== 'active') {
      params.catalogStatus = catalogStatus;
    }
    const response = await axiosWithRetry<ProductResponse>({
      method: 'get',
      url: `${this.baseURL}${endpoint}`,
      params,
      headers: await this.getHeaders()
    });
    return response.data;
  }

  /**
   * Admin product browser: every product that is announced or in pre-order by record,
   * each carrying `lifecycle` (`GET /admin/products/pre-release`). The catalog reads
   * (`/products*`) never carry lifecycle, so the browser joins this set onto its rows.
   */
  async getAdminPreReleaseProducts(): Promise<Product[]> {
    const response = await axiosWithRetry<{ data?: Product[] }>({
      method: 'get',
      url: `${this.baseURL}/admin/products/pre-release`,
      headers: await this.getHeaders()
    });
    return Array.isArray(response.data?.data) ? response.data.data : [];
  }

  async getProductMetadata(catalogStatus?: CatalogProductStatus, includeUnreleased?: boolean): Promise<{
    brands: Array<{ brand: string; count: number }>;
    categories: Array<{ category: string; count: number }>;
    productTypes: Array<{ productType: string; count: number }>;
    total: number;
  }> {
    const params: any = {};
    if (catalogStatus && catalogStatus !== 'active') {
      params.catalogStatus = catalogStatus;
    }
    if (includeUnreleased === false) params.includeUnreleased = 'false';
    const response = await axiosWithRetry<any>({
      method: 'get',
      url: `${this.baseURL}/products/metadata`,
      params,
      headers: await this.getHeaders()
    });
    return response.data?.data || response.data;
  }

  // Legacy camera methods for backward compatibility - now use products endpoints
  async getCameras(limit = 10, offset = 0): Promise<CameraResponse> {
    const response = await this.getProducts(limit, offset, 'camera');
    return response as CameraResponse;
  }

  async getCameraById(id: string): Promise<CameraProduct> {
    const response = await axios.get(`${this.baseURL}/products/${id}`, {
      headers: await this.getHeaders()
    });
    return response.data?.data || response.data;
  }

  async getLinkedProductVariants(id: string): Promise<Product[]> {
    const response = await axios.get(
      `${this.baseURL}/products/${encodeURIComponent(id)}/linked-variants`,
      { headers: await this.getHeaders() }
    );
    return response.data?.data || [];
  }

  /** One "Works with" group in full, paged (`GET /products/:id/compatibility`), for "Show all". */
  async getProductCompatibilityPage(
    id: string,
    group: 'lenses' | 'cameras' | 'accessories',
    offset: number,
    limit = 60,
  ): Promise<{
    items: Array<{ id: string; name: string; brand: string; image: string | null; mount: string | null; via: string }>;
    total: number;
    offset: number;
    nextOffset: number | null;
    truncated: boolean;
  }> {
    const response = await axios.get(`${this.baseURL}/products/${encodeURIComponent(id)}/compatibility`, {
      headers: await this.getHeaders(),
      params: { group, offset, limit },
    });
    return response.data?.data;
  }

  async reviewSuspiciousDiscount(
    productId: string,
    decision: 'approved' | 'rejected',
    extras?: { note?: string; advertisedPrice?: number; floor?: number }
  ): Promise<{ success: boolean; review: NonNullable<Product['pricing']>['suspiciousDiscountReview'] }> {
    const response = await axios.post(
      `${this.baseURL}/admin/products/${encodeURIComponent(productId)}/suspicious-discount-review`,
      { decision, ...extras },
      { headers: await this.getHeaders() }
    );
    return response.data;
  }

  async refreshProductMarketPricing(id: string): Promise<Product> {
    const response = await axios.post(
      `${this.baseURL}/products/${encodeURIComponent(id)}/market-pricing/refresh`,
      {},
      { headers: await this.getHeaders(), timeout: 180000 }
    );
    return response.data?.data || response.data;
  }

  async searchCameras(query: string, limit = 10, offset = 0): Promise<CameraResponse> {
    return this.searchProducts(query, limit, offset, 'camera') as Promise<CameraResponse>;
  }

  async searchProducts(
    query: string,
    limit = 10,
    offset = 0,
    productType?: 'camera' | 'lens' | 'accessory',
    catalogStatus?: CatalogProductStatus,
    brand?: string,
    sort?: CatalogSort,
    signal?: AbortSignal,
    includeUnreleased?: boolean
  ): Promise<ProductResponse> {
    const params: Record<string, string | number | undefined> = { q: query, limit, offset, productType, slim: 'true' };
    if (sort && sort !== 'name_asc') params.sort = sort;
    if (includeUnreleased === false) params.includeUnreleased = 'false';
    if (catalogStatus && catalogStatus !== 'active') {
      params.catalogStatus = catalogStatus;
    }
    if (brand?.trim()) {
      params.brand = brand.trim();
    }
    const response = await axiosWithRetry<ProductResponse>({
      method: 'get',
      url: `${this.baseURL}/products/search`,
      params,
      headers: await this.getHeaders(),
      signal,
    });
    return response.data;
  }

  async getCamerasByBrand(
    brand: string,
    limit = 10,
    offset = 0,
    category?: string
  ): Promise<CameraResponse> {
    return this.getProductsByBrand(brand, limit, offset, category, 'camera') as Promise<CameraResponse>;
  }

  async getProductsByBrand(
    brand: string,
    limit = 10,
    offset = 0,
    category?: string,
    productType?: 'camera' | 'lens' | 'accessory',
    catalogStatus?: CatalogProductStatus,
    sort?: CatalogSort,
    includeUnreleased?: boolean
  ): Promise<ProductResponse> {
    const params: any = { limit, offset, brand, productType, slim: 'true' };
    if (sort && sort !== 'name_asc') params.sort = sort;
    if (includeUnreleased === false) params.includeUnreleased = 'false';
    if (category) {
      params.category = category;
    }
    if (catalogStatus && catalogStatus !== 'active') {
      params.catalogStatus = catalogStatus;
    }

    const response = await axiosWithRetry<ProductResponse>({
      method: 'get',
      url: `${this.baseURL}/products/brand/${encodeURIComponent(brand)}`,
      params,
      headers: await this.getHeaders()
    });
    return response.data;
  }

  /**
   * Get products for the current store - filtered by the store's authorized dealer associations (brandsSold).
   * Only returns products from brands the store has added. Supports search, category, productType, and brand filters.
   */
  async getStoreCatalogCounts(options?: {
    catalogStatus?: CatalogProductStatus;
    /** When set, count discontinued-in-sync against this integration's list. */
    integrationId?: string | null;
  }): Promise<{
    brands: Array<{ brand: string; count: number }>;
    categories: Array<{ category: string; count: number }>;
    productTypes: Array<{ productType: string; count: number }>;
    total: number;
    /** Discontinued products the store still syncs, counted server-side. */
    discontinuedInSync: number;
  }> {
    const params: Record<string, string> = {};
    if (options?.catalogStatus && options.catalogStatus !== 'active') {
      params.catalogStatus = options.catalogStatus;
    }
    if (options?.integrationId) {
      params.integrationId = options.integrationId;
    }
    const query = new URLSearchParams(params).toString();
    const response = await makeApiCall(`/store/catalog-counts${query ? `?${query}` : ''}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to load store catalog counts');
    }
    const payload = await response.json();
    return (
      payload?.data || {
        brands: [],
        categories: [],
        productTypes: [],
        total: 0,
        discontinuedInSync: 0,
      }
    );
  }

  async getStoreProducts(
    limit = 25,
    offset = 0,
    options?: {
      search?: string;
      category?: string;
      productType?: 'camera' | 'lens' | 'accessory';
      brand?: string;
      catalogStatus?: CatalogProductStatus;
      /** ISO instant; only products made available to stores on or after it (flag `product_browser_added_filter`). */
      addedSince?: string;
      sort?: CatalogSort;
      /**
       * Active view only: the server merges the discontinued products this store
       * still syncs into the page, so the browser no longer reads the whole
       * catalog as `all` to show them (MOT-607).
       */
      includeSyncedDiscontinued?: boolean;
      /** The selection being edited, when it is one integration's own (flag `per_integration_sync_selection`). */
      integrationId?: string | null;
      signal?: AbortSignal;
    }
  ): Promise<ProductResponse> {
    const params: Record<string, string | number> = { limit, offset, slim: 'true' };
    if (options?.sort && options.sort !== 'name_asc') params.sort = options.sort;
    if (options?.includeSyncedDiscontinued) {
      params.includeSyncedDiscontinued = 'true';
      if (options.integrationId) params.integrationId = options.integrationId;
    }
    if (options?.addedSince) params.addedSince = options.addedSince;
    if (options?.search?.trim()) params.search = options.search.trim();
    if (options?.category?.trim()) params.category = options.category.trim();
    if (options?.productType) params.productType = options.productType;
    if (options?.brand?.trim()) params.brand = options.brand.trim();
    if (options?.catalogStatus && options.catalogStatus !== 'active') {
      params.catalogStatus = options.catalogStatus;
    }

    const query = new URLSearchParams(params as Record<string, string>).toString();
    const response = await axiosWithRetry<{ success: boolean; data: Product[]; count: number; pagination: ProductResponse['pagination'] }>({
      method: 'get',
      url: `${this.baseURL}/store/products-for-sync?${query}`,
      headers: await this.getHeaders(),
      signal: options?.signal,
    });
    const payload = response.data;
    return {
      success: payload.success ?? true,
      data: payload.data ?? [],
      count: payload.count ?? payload.data?.length ?? 0,
      pagination: payload.pagination ?? {
        limit,
        offset,
        total: payload.data?.length ?? 0,
        hasMore: false
      }
    };
  }

  /**
   * The store's sync selection, or, with an integration id, the selection that
   * integration pushes (its own list when it has one, the store's otherwise).
   */
  async getStoreSyncSelection(integrationId?: string | null): Promise<StoreSyncSelection> {
    const path = integrationId
      ? `/store/sync-selection?integrationId=${encodeURIComponent(integrationId)}`
      : '/store/sync-selection';
    const response = await makeApiCall(path, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch store sync selection');
    }
    const payload = await response.json();
    return (
      payload?.data || {
        selectedProductIds: [],
        syncAll: false,
      }
    );
  }

  /**
   * Catalog product ids the store has matched to its own SKUs or barcodes (SKU crosswalk plus
   * legacy SKU overrides), for the product browser's "Matched to my store" filter.
   */
  async getCrosswalkMatchedProductIds(integrationId?: string | null): Promise<string[]> {
    const path = integrationId
      ? `/store/crosswalk-matched-ids?integrationId=${encodeURIComponent(integrationId)}`
      : '/store/crosswalk-matched-ids';
    const response = await makeApiCall(path, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch matched product IDs');
    }
    const payload = await response.json();
    return payload?.ids || [];
  }

  async getFilteredProductIds(options?: {
    search?: string;
    category?: string;
    productType?: 'camera' | 'lens' | 'accessory';
    brand?: string;
    catalogStatus?: CatalogProductStatus;
    addedSince?: string;
    /** Same meaning as on `getStoreProducts`, so "select all" matches the grid. */
    includeSyncedDiscontinued?: boolean;
    integrationId?: string | null;
  }): Promise<string[]> {
    const params: Record<string, string> = {};
    if (options?.includeSyncedDiscontinued) {
      params.includeSyncedDiscontinued = 'true';
      if (options.integrationId) params.integrationId = options.integrationId;
    }
    if (options?.addedSince) params.addedSince = options.addedSince;
    if (options?.search?.trim()) params.search = options.search.trim();
    if (options?.category?.trim()) params.category = options.category.trim();
    if (options?.productType) params.productType = options.productType;
    if (options?.brand?.trim()) params.brand = options.brand.trim();
    if (options?.catalogStatus && options.catalogStatus !== 'active') {
      params.catalogStatus = options.catalogStatus;
    }
    const query = new URLSearchParams(params).toString();
    const response = await makeApiCall(`/store/sync-selection/filtered-ids?${query}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch filtered product IDs');
    }
    const payload = await response.json();
    return payload?.ids || [];
  }

  async addStoreProductsToSync(productIds: string[], integrationId?: string | null): Promise<any> {
    const response = await makeApiCall('/store/sync-selection/add', {
      method: 'POST',
      headers: {
        ...(await this.getHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(integrationId ? { productIds, integrationId } : { productIds }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const message = payload?.message || payload?.error?.message || 'Failed to add products to sync selection';
      const code = payload?.code || payload?.error?.code;
      if (code === 'SYNC_SELECTION_CAP' || code === 'SYNC_SELECTION_BRAND_CAP') {
        throw new SyncSelectionCapError(message, code, {
          limit: Number(payload.limit),
          current: Number(payload.current),
          attempted: Number(payload.attempted),
          planName: typeof payload.planName === 'string' ? payload.planName : null,
        });
      }
      throw new Error(message);
    }
    return response.json();
  }

  /** The variant group of a product, limited to what the store can select (flag `sync_select_variant_group`). */
  async getStoreVariantGroup(productId: string, integrationId?: string | null): Promise<StoreVariantGroup> {
    const query = integrationId ? `?integrationId=${encodeURIComponent(integrationId)}` : '';
    const response = await makeApiCall(
      `/store/products/${encodeURIComponent(productId)}/variant-group${query}`,
      { headers: await this.getHeaders() },
    );
    if (!response.ok) {
      throw new Error('Failed to fetch variant group');
    }
    const payload = await response.json();
    return payload?.data;
  }

  async removeStoreProductsFromSync(productIds: string[], integrationId?: string | null): Promise<any> {
    const response = await makeApiCall('/store/sync-selection/remove', {
      method: 'POST',
      headers: {
        ...(await this.getHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(integrationId ? { productIds, integrationId } : { productIds }),
    });
    if (!response.ok) {
      throw new Error('Failed to remove products from sync selection');
    }
    return response.json();
  }

  /** The store's selection plus every integration that keeps its own. */
  async getStoreSyncSelections(): Promise<StoreSyncSelectionSummary[]> {
    const response = await makeApiCall('/store/sync-selections', {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch sync selections');
    }
    const payload = await response.json();
    return payload?.data?.selections || [];
  }

  /**
   * Put one integration back on the store's selection, or off it onto its own
   * list (seeded from the store's).
   */
  async setIntegrationSyncSelectionInheritance(integrationId: string, inherit: boolean): Promise<any> {
    const response = await makeApiCall('/store/sync-selection/inherit', {
      method: 'PUT',
      headers: {
        ...(await this.getHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ integrationId, inherit }),
    });
    if (!response.ok) {
      throw new Error('Failed to update the sync selection for this integration');
    }
    return response.json();
  }

  private async throwApiError(response: Response, fallback: string): Promise<never> {
    let detail = '';
    try {
      const body = await response.clone().json();
      detail =
        body?.error?.message ||
        body?.message ||
        (typeof body?.error === 'string' ? body.error : '');
    } catch {
      // ignore non-JSON error bodies
    }
    const suffix = detail ? `: ${detail}` : '';
    throw new Error(`${fallback} (${response.status})${suffix}`);
  }

  async getStoreProductOverride(productId: string): Promise<StoreProductOverride | null> {
    const response = await makeApiCall(`/store/product-overrides?productId=${encodeURIComponent(productId)}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to fetch product override');
    }
    const payload = await response.json();
    return payload.override || null;
  }

  /** The store's pre-order go-live for a product (flag `store_preorder_go_live`, MOT-766). */
  async getStorePreorderGoLive(productId: string): Promise<StorePreorderGoLiveView> {
    const response = await makeApiCall(`/store/product-overrides/${encodeURIComponent(productId)}/preorder-go-live`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to load the pre-order go-live');
    }
    const payload = await response.json();
    return payload.data as StorePreorderGoLiveView;
  }

  /** Set, or with null clear, the store's pre-order go-live for a product (owner or admin). */
  async saveStorePreorderGoLive(productId: string, goLive: StorePreorderGoLiveInput | null): Promise<StorePreorderGoLiveView> {
    const response = await makeApiCall(`/store/product-overrides/${encodeURIComponent(productId)}/preorder-go-live`, {
      method: 'PUT',
      headers: { ...(await this.getHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify({ goLive }),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to save the pre-order go-live');
    }
    const payload = await response.json();
    return payload.data as StorePreorderGoLiveView;
  }

  /** The store's own related and upsell picks for a product (flag `retailer_related_products`, MOT-705). */
  async getStoreRelatedPicks(productId: string): Promise<StoreRelatedPicksView> {
    const response = await makeApiCall(`/store/related-products/${encodeURIComponent(productId)}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to load related products');
    }
    const payload = await response.json();
    return payload.data as StoreRelatedPicksView;
  }

  /** Replace the store's picks for a product, in order (owner or admin). */
  async saveStoreRelatedPicks(productId: string, picks: { related: string[]; upsell: string[] }): Promise<StoreRelatedPicksView> {
    const response = await makeApiCall(`/store/related-products/${encodeURIComponent(productId)}`, {
      method: 'PUT',
      headers: { ...(await this.getHeaders()), 'Content-Type': 'application/json' },
      body: JSON.stringify(picks),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to save related products');
    }
    const payload = await response.json();
    return payload.data as StoreRelatedPicksView;
  }

  /** Which related-products destinations exist on a Shopify shop (flag `retailer_related_products`). */
  async getShopifyRelatedProductsStatus(integrationId: string): Promise<ShopifyRelatedProductsStatus> {
    const response = await makeApiCall(`/integrations/${encodeURIComponent(integrationId)}/shopify/related-products/status`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to read the shop\'s related-products fields');
    }
    const payload = await response.json();
    return payload.data as ShopifyRelatedProductsStatus;
  }

  /** The spec labels the push matches for a product (flag `store_override_specs_push`, MOT-413). */
  async getStoreProductSpecLabels(productId: string): Promise<string[]> {
    const response = await makeApiCall(`/store/product-overrides/${encodeURIComponent(productId)}/spec-labels`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to fetch spec labels');
    }
    const payload = await response.json();
    return Array.isArray(payload.labels) ? payload.labels.map(String) : [];
  }

  async getShopifyPricingAudit(options?: {
    integrationId?: string;
    includeOk?: boolean;
  }): Promise<ShopifyMapAuditResponse> {
    const params = new URLSearchParams();
    if (options?.integrationId) params.set('integrationId', options.integrationId);
    if (options?.includeOk) params.set('includeOk', 'true');
    const query = params.toString();
    const response = await makeApiCall(`/store/shopify-pricing-audit${query ? `?${query}` : ''}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to load Shopify pricing audit');
    }
    return response.json();
  }

  async runShopifyPricingAudit(integrationId?: string): Promise<{
    success: boolean;
    inProgress?: boolean;
    organizationId: string;
    results: Array<{ summary: ShopifyMapAuditSummary; findings: ShopifyMapAuditFinding[] }>;
  }> {
    const response = await makeApiCall('/store/shopify-pricing-audit', {
      method: 'POST',
      headers: {
        ...(await this.getHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(integrationId ? { integrationId } : {}),
    });
    if (response.status === 202 || response.status === 409 || response.status === 502 || response.status === 503 || response.status === 504) {
      const payload = response.status === 202 || response.status === 409
        ? await response.json().catch(() => ({}))
        : {};
      return {
        success: true,
        inProgress: true,
        organizationId: payload.organizationId || '',
        results: Array.isArray(payload.results) ? payload.results : [],
      };
    }
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to run Shopify pricing audit');
    }
    return response.json();
  }

  async resyncShopifyAuditFindings(options?: {
    integrationId?: string;
    targets?: Array<{ productId: string; integrationId: string }>;
  }): Promise<{
    success: boolean;
    attempted: number;
    pushed: number;
    failed: number;
    errors: string[];
    productIds: string[];
  }> {
    const integrationId = options?.integrationId;
    const targets = options?.targets;
    const response = await makeApiCall('/store/shopify-pricing-audit/resync', {
      method: 'POST',
      headers: {
        ...(await this.getHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...(integrationId ? { integrationId } : {}),
        ...(targets && targets.length > 0 ? { targets } : {}),
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok && payload?.attempted == null) {
      throw new Error(
        payload?.error?.message || payload?.message || 'Failed to push Motzu prices to Shopify',
      );
    }
    return {
      success: payload.success === true,
      attempted: Number(payload.attempted) || 0,
      pushed: Number(payload.pushed) || 0,
      failed: Number(payload.failed) || 0,
      errors: Array.isArray(payload.errors) ? payload.errors : [],
      productIds: Array.isArray(payload.productIds) ? payload.productIds : [],
    };
  }

  async getErpSyncAudit(productId: string): Promise<ErpSyncAuditResponse> {
    const response = await makeApiCall(`/store/erp-sync-audit?productId=${encodeURIComponent(productId)}`, {
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to fetch store listing sync audit');
    }
    return response.json();
  }

  /** Reset Shopify link: removes the product's Motzu mapping (nothing in Shopify) and queues a one-product sync. Owner/admin. */
  async resetShopifyLink(integrationId: string, productId: string): Promise<ShopifyLinkResetResult> {
    const response = await makeApiCall(
      `/integrations/${encodeURIComponent(integrationId)}/shopify-mappings/${encodeURIComponent(productId)}/reset`,
      {
        method: 'POST',
        headers: {
          ...(await this.getHeaders()),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      },
    );
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to reset the Shopify link');
    }
    const payload = await response.json();
    return payload.data as ShopifyLinkResetResult;
  }

  /**
   * Resume Motzu pricing (MOT-470, flag `shopify_respect_store_prices`): the next push writes Motzu's
   * price and compare-at again on a listing whose price the store changed in Shopify. Owner/admin.
   */
  async resumeShopifyPricing(integrationId: string, productId: string): Promise<{ mappings: number }> {
    const response = await makeApiCall(
      `/integrations/${encodeURIComponent(integrationId)}/shopify-mappings/${encodeURIComponent(productId)}/resume-pricing`,
      {
        method: 'POST',
        headers: {
          ...(await this.getHeaders()),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      },
    );
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to resume Motzu pricing');
    }
    const payload = await response.json();
    return payload.data as { mappings: number };
  }

  async forceErpResync(
    productId: string,
    integrationId?: string,
    options?: ErpResyncOptions
  ): Promise<ErpProductResyncResponse> {
    const response = await makeApiCall('/store/erp-resync', {
      method: 'POST',
      headers: {
        ...(await this.getHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        productId,
        ...(integrationId ? { integrationId } : {}),
        ...(options?.forceDescription ? { forceDescription: true } : {}),
        ...(options?.forceImages ? { forceImages: true } : {}),
        ...(options?.forcePricing ? { forcePricing: true } : {}),
        ...(options?.forceCategories ? { forceCategories: true } : {}),
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (Array.isArray(payload?.results)) {
      return payload as ErpProductResyncResponse;
    }
    const message =
      payload?.error?.message ||
      payload?.message ||
      'Failed to resync product to the store';
    throw new Error(message);
  }

  async upsertStoreProductOverride(
    productId: string,
    payload: Partial<StoreProductOverride>
  ): Promise<StoreProductOverride> {
    const response = await makeApiCall(`/store/product-overrides/${encodeURIComponent(productId)}`, {
      method: 'PUT',
      headers: {
        ...(await this.getHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to save product override');
    }
    const data = await response.json();
    return data.override as StoreProductOverride;
  }

  async deleteStoreProductOverride(productId: string): Promise<void> {
    const response = await makeApiCall(`/store/product-overrides/${encodeURIComponent(productId)}`, {
      method: 'DELETE',
      headers: await this.getHeaders(),
    });
    if (!response.ok) {
      await this.throwApiError(response, 'Failed to delete product override');
    }
  }

  async generateProductDescription(product: {
    name?: string;
    brand?: string;
    model?: string;
    sku?: string;
    category?: string;
    productType?: string;
    description?: string;
    specifications?: Record<string, string>;
  }): Promise<GeneratedProductDescription> {
    const response = await makeApiCall('/ai/generate-product-description', {
      method: 'POST',
      headers: {
        ...(await this.getHeaders()),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ product }),
    });
    if (!response.ok) {
      throw new Error('Failed to generate product description');
    }
    const payload = await response.json();
    return payload?.data as GeneratedProductDescription;
  }

  async getCamerasByCategory(category: string, limit = 10, offset = 0): Promise<CameraResponse> {
    return this.getProductsByCategory(category, limit, offset, 'camera') as Promise<CameraResponse>;
  }

  async getProductsByCategory(
    category: string,
    limit = 10,
    offset = 0,
    productType?: 'camera' | 'lens' | 'accessory',
    catalogStatus?: CatalogProductStatus,
    sort?: CatalogSort,
    includeUnreleased?: boolean
  ): Promise<ProductResponse> {
    const params: Record<string, string | number | undefined> = { limit, offset, productType, slim: 'true' };
    if (sort && sort !== 'name_asc') params.sort = sort;
    if (includeUnreleased === false) params.includeUnreleased = 'false';
    if (catalogStatus && catalogStatus !== 'active') {
      params.catalogStatus = catalogStatus;
    }
    const response = await axiosWithRetry<ProductResponse>({
      method: 'get',
      url: `${this.baseURL}/products/category/${encodeURIComponent(category)}`,
      params,
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async getCategoriesForBrand(brand: string, productType?: 'camera' | 'lens' | 'accessory'): Promise<string[]> {
    const response = await axiosWithRetry<ProductResponse>({
      method: 'get',
      url: `${this.baseURL}/products/brand/${encodeURIComponent(brand)}`,
      params: { limit: 1000, offset: 0, productType },
      headers: await this.getHeaders()
    });
    
    // Extract unique categories from the response
    const categories: string[] = Array.from(
      new Set(response.data.data.map((product: Product) => product.category))
    );
    return categories.sort();
  }

  async getCameraStats(productType?: 'camera' | 'lens' | 'accessory'): Promise<{
    total: number;
    byCategory: Record<string, number>;
    byBrand: Record<string, number>;
    priceRange: { min: number; max: number; avg: number };
  }> {
    const response = await axios.get(`${this.baseURL}/products/stats`, {
      params: { productType },
      headers: await this.getHeaders()
    });
    return response.data.data || response.data;
  }

  // API Key management methods
  async getApiKeys(): Promise<ApiKey[]> {
    const response = await axios.get(`${this.baseURL}/api-keys`, {
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async getApiKeyScopeOptions(forUserId?: string): Promise<ApiKeyScopeOptions> {
    const response = await axios.get(`${this.baseURL}/api-keys/scope-options`, {
      headers: await this.getHeaders(),
      ...(forUserId ? { params: { userId: forUserId } } : {}),
    });
    return response.data;
  }

  async createApiKey(name: string, permissions?: string[], dataScope?: ApiKeyDataScope | null): Promise<ApiKey> {
    const response = await axios.post(`${this.baseURL}/api-keys`, {
      name,
      ...(permissions && permissions.length > 0 ? { permissions } : {}),
      ...(dataScope ? { dataScope } : {}),
    }, {
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async updateApiKey(
    id: string,
    updates: { name?: string; permissions?: string[]; isActive?: boolean; dataScope?: ApiKeyDataScope | null }
  ): Promise<ApiKey> {
    const response = await axios.patch(`${this.baseURL}/api-keys/${id}`, updates, {
      headers: await this.getHeaders(),
    });
    return response.data;
  }

  async deleteApiKey(id: string): Promise<void> {
    await axios.delete(`${this.baseURL}/api-keys/${id}`, {
      headers: await this.getHeaders()
    });
  }

  // MAP Pricing methods
  async getMapPricing(cameraId?: string): Promise<MapPricing[]> {
    const params = cameraId ? { cameraId } : {};
    const response = await axios.get(`${this.baseURL}/map-pricing`, {
      params,
      headers: await this.getHeaders()
    });
    // Ensure we always return an array
    const data = response.data;
    return Array.isArray(data) ? data : (data.data || []);
  }

  /**
   * Get pricing data for a specific model/productId
   * Returns pricing data in format suitable for ProductPricingBadge component
   */
  async getPricingByModel(model: string, productId?: string, brand?: string, sku?: string, mpn?: string): Promise<any | null> {
    const params: any = { model };
    if (productId) {
      params.productId = productId;
    }
    if (brand) {
      params.brand = brand;
    }
    if (sku) {
      params.sku = sku;
    }
    if (mpn) {
      params.mpn = mpn;
    }
    const response = await axiosWithRetry<any>({
      method: 'get',
      url: `${this.baseURL}/map-pricing/by-model`,
      params,
      headers: await this.getHeaders()
    });
    return response.data.data || null;
  }

  async getPricingByModels(
    items: Array<{ model: string; productId?: string; brand?: string; sku?: string; mpn?: string }>
  ): Promise<Record<string, any | null>> {
    const response = await axiosWithRetry<any>({
      method: 'post',
      url: `${this.baseURL}/map-pricing/by-models`,
      data: { items },
      headers: await this.getHeaders()
    });
    return response.data.data || {};
  }

  /**
   * Get pricing history (MAP ingest log + successive sheet versions).
   */
  async getPricingHistory(brand?: string, model?: string, limit?: number, productId?: string): Promise<any[]> {
    const params: any = {};
    if (brand) params.brand = brand;
    if (model) params.model = model;
    if (productId) params.productId = productId;
    if (limit) params.limit = limit;
    
    const response = await axios.get(`${this.baseURL}/map-pricing/history`, {
      params,
      headers: await this.getHeaders()
    });
    return response.data.data || [];
  }

  async createMapPricing(data: Omit<MapPricing, 'id' | 'createdAt' | 'updatedAt'>): Promise<MapPricing> {
    const response = await axios.post(`${this.baseURL}/map-pricing`, data, {
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async updateMapPricing(id: string, data: Partial<MapPricing>): Promise<MapPricing> {
    const response = await axios.put(`${this.baseURL}/map-pricing/${id}`, data, {
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async deleteMapPricing(id: string): Promise<void> {
    await axios.delete(`${this.baseURL}/map-pricing/${id}`, {
      headers: await this.getHeaders()
    });
  }

  // Admin API methods
  async updateCamera(cameraId: string, updates: Partial<CameraProduct>): Promise<void> {
    await axios.put(`${this.baseURL}/admin/cameras/${cameraId}`, updates, {
      headers: await this.getHeaders()
    });
  }

  async deleteCamera(cameraId: string): Promise<void> {
    await axios.delete(`${this.baseURL}/admin/cameras/${cameraId}`, {
      headers: await this.getHeaders()
    });
  }

  async createCamera(cameraData: Omit<CameraProduct, 'id' | 'createdAt' | 'updatedAt'>): Promise<CameraProduct> {
    const response = await axios.post(`${this.baseURL}/admin/cameras`, cameraData, {
      headers: await this.getHeaders()
    });
    return response.data.data;
  }

  async getAllUsers(): Promise<any[]> {
    const response = await axios.get(`${this.baseURL}/admin/users`, {
      headers: await this.getHeaders()
    });
    return response.data.users;
  }

  async updateUserRole(userId: string, role: 'admin' | 'user'): Promise<void> {
    await axios.put(`${this.baseURL}/admin/users/${userId}/role`, { role }, {
      headers: await this.getHeaders()
    });
  }

  async sendNotificationToAll(type: string, title: string, message: string, data?: any): Promise<void> {
    await axios.post(`${this.baseURL}/admin/notifications`, { type, title, message, data }, {
      headers: await this.getHeaders()
    });
  }

  async getSystemStats(): Promise<any> {
    try {
      const response = await axios.get(`${this.baseURL}/admin/stats`, {
        headers: await this.getHeaders()
      });
      // Prefer nested shape if present
      return response.data.stats || response.data;
    } catch (e) {
      // Fallback for legacy route while backend route stabilizes
      const response = await axios.get(`${this.baseURL}/admin/legacy/stats`, {
        headers: await this.getHeaders()
      });
      return response.data.stats || response.data;
    }
  }

  // Accessory-specific methods
  async getAccessories(limit = 10, offset = 0): Promise<ProductResponse> {
    return this.getProducts(limit, offset, 'accessory');
  }

  async getAccessoriesByType(accessoryType: string, limit = 10, offset = 0): Promise<ProductResponse> {
    const endpoint = process.env.NODE_ENV === 'development' ? '/products' : '/cameras';
    const response = await axios.get(`${this.baseURL}${endpoint}`, {
      params: { 
        limit, 
        offset, 
        productType: 'accessory',
        accessoryType 
      },
      headers: await this.getHeaders()
    });
    return response.data;
  }

  // Feed subscription methods
  async getFeedSubscriptions(): Promise<any[]> {
    const response = await axios.get(`${this.baseURL}/feed-subscriptions`, {
      headers: await this.getHeaders()
    });
    return response.data.data;
  }

  async createFeedSubscription(subscription: any): Promise<any> {
    const response = await axios.post(`${this.baseURL}/feed-subscriptions`, subscription, {
      headers: await this.getHeaders()
    });
    return response.data.data;
  }

  async updateFeedSubscription(subscriptionId: string, updates: any): Promise<any> {
    const response = await axios.put(`${this.baseURL}/feed-subscriptions/${subscriptionId}`, updates, {
      headers: await this.getHeaders()
    });
    return response.data.data;
  }

  async deleteFeedSubscription(subscriptionId: string): Promise<void> {
    await axios.delete(`${this.baseURL}/feed-subscriptions/${subscriptionId}`, {
      headers: await this.getHeaders()
    });
  }

  async getFeedSubscriptionStats(): Promise<any> {
    const response = await axios.get(`${this.baseURL}/feed-subscriptions?stats=true`, {
      headers: await this.getHeaders()
    });
    return response.data.data;
  }

  // Shopify sync methods
  async triggerShopifySync(shopDomain: string, action: 'sync' | 'monitor' | 'stats'): Promise<any> {
    const response = await axios.post(`${this.baseURL}/shopify-sync`, {
      shopDomain,
      action
    }, {
      headers: await this.getHeaders()
    });
    return response.data.data;
  }

  // Integration methods (end user endpoints)
  async getIntegrations(): Promise<any> {
    const response = await axios.get(`${this.baseURL}/integrations`, {
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async getIntegration(id: string): Promise<any> {
    const response = await axios.get(`${this.baseURL}/integrations/${id}`, {
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async createIntegration(integration: any): Promise<any> {
    const response = await axios.post(`${this.baseURL}/integrations`, integration, {
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async updateIntegration(id: string, updates: any): Promise<any> {
    const response = await axios.put(`${this.baseURL}/integrations/${id}`, updates, {
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async deleteIntegration(id: string): Promise<void> {
    await axios.delete(`${this.baseURL}/integrations/${id}`, {
      headers: await this.getHeaders()
    });
  }

  // Organization methods
  async getOrganization(organizationId: string): Promise<any> {
    const headers = await this.getHeaders();
    const response = await makeApiCall(`/organizations/${organizationId}`, {
      headers,
      method: 'GET',
    });

    if (response.status === 401) {
      // Clear session cache and retry once with a fresh token
      localStorage.removeItem('api_session_id');
      localStorage.removeItem('api_session_expires');
      const retryHeaders = await this.getHeaders();
      const retryResponse = await makeApiCall(`/organizations/${organizationId}`, {
        headers: retryHeaders,
        method: 'GET',
      });
      if (!retryResponse.ok) {
        throw new Error(`Failed to fetch organization: ${retryResponse.status}`);
      }
      return retryResponse.json();
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch organization: ${response.status}`);
    }

    return response.json();
  }

  async updateOrganization(organizationId: string, updates: any): Promise<any> {
    const response = await axios.put(`${this.baseURL}/organizations/${organizationId}`, updates, {
      headers: await this.getHeaders()
    });
    return response.data;
  }

  async generateCodeSnippet(payload: {
    language: string;
    method: string;
    url: string;
    headers?: Record<string, string>;
    body?: unknown;
    description?: string;
  }): Promise<{ snippet: string; language: string }> {
    const response = await makeApiCall('/code-snippets/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getHeaders()),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to generate code snippet');
    }

    const data = await response.json();
    return data.data;
  }

  async generateAICodeSnippet(payload: {
    language: string;
    method: string;
    url: string;
    headers?: Record<string, string>;
    body?: unknown;
    description?: string;
    instructions?: string;
  }): Promise<{ snippet: string; language: string; source: string }> {
    const response = await makeApiCall('/code-snippets/generate/ai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await this.getHeaders()),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to generate AI code snippet');
    }

    const data = await response.json();
    return data.data;
  }

  // =========================================================================
  // Product Issue Reports
  // =========================================================================

  async submitProductIssue(issue: {
    productId: string;
    productSku: string;
    productName: string;
    productBrand: string;
    category: string;
    description: string;
    suggestedValue?: string;
    /** 'sync_run' marks a one-click report filed from a sync run row. */
    source?: 'manual' | 'sync_run';
    /** Stable key so repeat reports of the same problem collapse into one. */
    sourceKey?: string;
    issueCode?: string;
    syncRunId?: string;
    integrationId?: string;
    integrationName?: string;
  }): Promise<{ success: boolean; duplicate?: boolean; data?: any }> {
    const response = await axiosWithRetry<{ success: boolean; duplicate?: boolean; data?: any }>({
      method: 'POST',
      url: `${this.baseURL}/product-issues`,
      headers: await this.getHeaders(),
      data: issue,
    });
    return response.data;
  }

  /** sourceKeys already reported (open or in review) for the caller's org. */
  async getReportedSyncIssueKeys(): Promise<string[]> {
    const response = await axiosWithRetry<{ data: string[] }>({
      method: 'GET',
      url: `${this.baseURL}/product-issues/sync-reported`,
      headers: await this.getHeaders(),
    });
    return response.data?.data || [];
  }

  async getProductIssues(productId: string): Promise<any[]> {
    const response = await axiosWithRetry<{ data: any[] }>({
      method: 'GET',
      url: `${this.baseURL}/product-issues/product/${productId}`,
      headers: await this.getHeaders(),
    });
    return response.data?.data || [];
  }

  async getMyIssueReports(): Promise<any[]> {
    const response = await axiosWithRetry<{ data: any[] }>({
      method: 'GET',
      url: `${this.baseURL}/product-issues/my-reports`,
      headers: await this.getHeaders(),
    });
    return response.data?.data || [];
  }
}

export const apiService = new ApiService();
